import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  HStack, Heading, Text, VStack, Icon, Divider, Box,
  Select, SelectTrigger, SelectInput, SelectIcon, SelectPortal,
  SelectBackdrop, SelectContent, SelectDragIndicatorWrapper,
  SelectDragIndicator, SelectItem, Button, ButtonText,
} from '@gluestack-ui/themed';
import {
  Users, Clock, ChevronDown, Mail,
} from 'lucide-react-native';
import type { WorkStateFilter } from '../types';

interface EngagementHeaderProps {
  totalStudents: number;
  stalledCount: number;
  availableModules: Array<{ id: string; title: string }>;
  availableVersions: string[];
  selectedModuleId?: string;
  selectedModuleVersion?: string;
  selectedWorkState: WorkStateFilter;
  onSelectModule: (moduleId: string) => void;
  onSelectModuleVersion: (moduleVersion: string) => void;
  onSelectWorkState: (workState: WorkStateFilter) => void;
  onEmailAll: () => void;
  isEmailAllDisabled: boolean;
  emailAllDisabledHint?: string;
}

const EngagementHeader: React.FC<EngagementHeaderProps> = ({
  totalStudents,
  stalledCount,
  availableModules,
  availableVersions,
  selectedModuleId,
  selectedModuleVersion,
  selectedWorkState,
  onSelectModule,
  onSelectModuleVersion,
  onSelectWorkState,
  onEmailAll,
  isEmailAllDisabled,
  emailAllDisabledHint,
}) => {
  const { t } = useTranslation();
  const showVersionFilter = !!selectedModuleId && availableVersions.length > 0;

  return (
    <HStack
      px="$6"
      py="$3"
      borderBottomWidth={1}
      borderBottomColor="$borderLight"
      alignItems="center"
      justifyContent="space-between"
      bg="$white"
    >
      <HStack alignItems="center" space="md" flex={1}>
        <Icon as={Users} size="md" color="$textSubtle" />
        <VStack mr="$6">
          <Heading size="md" color="$textHeading">
            Engagement
          </Heading>
          <Text size="sm" color="$textSubtle">
            Monitor student progress and provide feedback
          </Text>
        </VStack>

        <HStack space="sm" alignItems="center" flexShrink={1}>
          <Box w={220}>
            <Select
              selectedValue={selectedModuleId || 'all'}
              onValueChange={(val) => onSelectModule(val === 'all' ? '' : val)}
            >
              <SelectTrigger variant="outline" size="sm" bg="$bgSubtle" testID="filter-module">
                <SelectInput placeholder={t('lecturer.engagement.filters.allModules')} />
                <SelectIcon as={ChevronDown} mr="$3" />
              </SelectTrigger>
              <SelectPortal>
                <SelectBackdrop />
                <SelectContent>
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  <SelectItem label={t('lecturer.engagement.filters.allModules')} value="all" />
                  {availableModules.map((m) => (
                    <SelectItem key={m.id} label={m.title} value={m.id} />
                  ))}
                </SelectContent>
              </SelectPortal>
            </Select>
          </Box>

          {showVersionFilter && (
            <Box w={160}>
              <Select
                selectedValue={selectedModuleVersion || 'all'}
                onValueChange={(val) => onSelectModuleVersion(val === 'all' ? '' : val)}
              >
                <SelectTrigger variant="outline" size="sm" bg="$bgSubtle" testID="filter-version">
                  <SelectInput placeholder={t('lecturer.engagement.filters.allVersions')} />
                  <SelectIcon as={ChevronDown} mr="$3" />
                </SelectTrigger>
                <SelectPortal>
                  <SelectBackdrop />
                  <SelectContent>
                    <SelectDragIndicatorWrapper>
                      <SelectDragIndicator />
                    </SelectDragIndicatorWrapper>
                    <SelectItem label={t('lecturer.engagement.filters.allVersions')} value="all" />
                    {availableVersions.map((version) => (
                      <SelectItem key={version} label={version} value={version} />
                    ))}
                  </SelectContent>
                </SelectPortal>
              </Select>
            </Box>
          )}

          <Box w={140}>
            <Select
              selectedValue={selectedWorkState}
              onValueChange={(val) => onSelectWorkState(val as WorkStateFilter)}
            >
              <SelectTrigger variant="outline" size="sm" bg="$bgSubtle" testID="filter-work-state">
                <SelectInput placeholder={t('lecturer.engagement.filters.workStateAll')} />
                <SelectIcon as={ChevronDown} mr="$3" />
              </SelectTrigger>
              <SelectPortal>
                <SelectBackdrop />
                <SelectContent>
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  <SelectItem label={t('lecturer.engagement.filters.workStateAll')} value="all" />
                  <SelectItem label={t('lecturer.engagement.filters.workStateActive')} value="active" />
                  <SelectItem label={t('lecturer.engagement.filters.workStateStalled')} value="stalled" />
                </SelectContent>
              </SelectPortal>
            </Select>
          </Box>
        </HStack>

        <Button
          variant="outline"
          action="secondary"
          size="sm"
          onPress={onEmailAll}
          isDisabled={isEmailAllDisabled}
          accessibilityHint={isEmailAllDisabled ? emailAllDisabledHint : undefined}
          testID="engagement-email-all"
        >
          <HStack space="xs" alignItems="center">
            <Icon as={Mail} size="xs" color="$textSubtle" />
            <ButtonText>{t('lecturer.engagement.email.button')}</ButtonText>
          </HStack>
        </Button>
      </HStack>

      <HStack
        space="lg"
        bg="$bgSubtle"
        p="$2"
        borderRadius="$md"
        borderWidth={1}
        borderColor="$borderLight"
      >
        <VStack alignItems="center" px="$2" testID="stat-students">
          <HStack space="xs" alignItems="center">
            <Icon as={Users} size="xs" color="$textHint" />
            <Text size="xs" fontWeight="bold">
              {totalStudents}
            </Text>
          </HStack>
          <Text size="2xs" color="$textSubtle">
            Active Students
          </Text>
        </VStack>
        <Divider orientation="vertical" />
        <VStack alignItems="center" px="$2" testID="stat-stalled">
          <HStack space="xs" alignItems="center">
            <Icon as={Clock} size="xs" color="$statusWarning" />
            <Text size="xs" fontWeight="bold" color="$statusWarning">
              {stalledCount}
            </Text>
          </HStack>
          <Text size="2xs" color="$textSubtle">
            Stalled
          </Text>
        </VStack>
      </HStack>
    </HStack>
  );
};

export default EngagementHeader;
